import { acquireToken } from '../auth/msal.js';
import { appConfig } from './app-config.js';

/**
 * The single path through which the SPA reaches the API.
 *
 * Every call carries a fresh Entra access token. Nothing here stores one:
 * `acquireToken` reads MSAL's sessionStorage cache and refreshes silently when
 * it is close to expiry, so a long-running console tab keeps working without
 * the user noticing, and a revoked session stops working within one token
 * lifetime.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
  correlationId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  /** Quote this to support; it ties back to the server log and audit events. */
  readonly correlationId: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.correlationId = body.correlationId ?? null;
  }

  /** True when the user lacks the permission, as opposed to being signed out. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get needsApproval(): boolean {
    return this.code === 'APPROVAL_REQUIRED';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Sent on the first call after sign-in; provisions the user and session. */
  signin?: boolean;
  headers?: Record<string, string>;
}

async function buildHeaders(options: RequestOptions): Promise<Headers> {
  const headers = new Headers(options.headers);
  headers.set('accept', 'application/json');
  if (options.body !== undefined) headers.set('content-type', 'application/json');
  if (options.signin) headers.set('x-csa-signin', '1');
  headers.set('authorization', `Bearer ${await acquireToken(appConfig().auth)}`);
  return headers;
}

/**
 * Performs the request, retrying exactly once on a 401.
 *
 * One retry, not a loop: the first 401 is almost always a token that expired
 * between being read from cache and reaching the server, which a forced
 * refresh fixes. A second 401 means the session is genuinely gone, and
 * retrying again would spin.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> =>
    fetch(path, {
      method: options.method ?? 'GET',
      headers: await buildHeaders(options),
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal ?? null,
      credentials: 'same-origin',
    });

  let response = await send();
  if (response.status === 401) {
    response = await send();
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    throw new ApiError(response.status, await readErrorBody(response));
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return (await response.text()) as T;
  }
  return (await response.json()) as T;
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    const parsed = (await response.json()) as Partial<ApiErrorBody>;
    if (parsed && typeof parsed.message === 'string') {
      return {
        code: parsed.code ?? 'UNKNOWN',
        message: parsed.message,
        details: parsed.details,
        correlationId: parsed.correlationId,
      };
    }
  } catch {
    // A non-JSON error body means something upstream of the application
    // answered -- an ALB 502, or a WAF block page.
  }
  return {
    code: response.status >= 500 ? 'UPSTREAM_ERROR' : 'REQUEST_FAILED',
    message:
      response.status >= 500
        ? 'The server could not complete the request. Try again shortly.'
        : `Request failed with status ${response.status}`,
  };
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * Opens an authenticated SSE stream.
 *
 * The native EventSource cannot set an Authorization header, and this API
 * takes bearer tokens rather than cookies, so the stream is read from a fetch
 * body instead. That also gives an AbortSignal, which EventSource does not
 * have, and is what lets navigating away actually close the server-side
 * stream rather than leaving it heartbeating into nothing.
 */
export async function openEventStream(
  path: string,
  handlers: {
    onEvent: (event: { type: string; data: unknown }) => void;
    onError?: (err: unknown) => void;
  },
  signal: AbortSignal,
): Promise<void> {
  const headers = await buildHeaders({});
  headers.set('accept', 'text/event-stream');

  const response = await fetch(path, { headers, signal, credentials: 'same-origin' });
  if (!response.ok || !response.body) {
    throw new ApiError(response.status, await readErrorBody(response));
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += value;

      // SSE frames are separated by a blank line. A frame can arrive split
      // across chunks, so only complete frames are consumed and the remainder
      // stays buffered.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseFrame(frame);
        if (parsed) handlers.onEvent(parsed);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } catch (err) {
    if (signal.aborted) return;
    handlers.onError?.(err);
    throw err;
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(frame: string): { type: string; data: unknown } | null {
  let type = 'message';
  const dataLines: string[] = [];

  for (const line of frame.split('\n')) {
    // Comment frames are the keep-alive heartbeat; they carry no payload but
    // their arrival is what proves the stream is still live.
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) type = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }

  if (dataLines.length === 0) return null;
  const raw = dataLines.join('\n');
  try {
    return { type, data: JSON.parse(raw) };
  } catch {
    return { type, data: raw };
  }
}
